import { useEffect, useRef, useState } from 'react';
import { useApp } from '../store';
import { confirmDialog } from '../confirm';
import { displayName } from '../people';
import VendorTag from './VendorTag';
import { asDate } from '../utils';
import { can } from '../permissions';
import Icon, { type IconName } from './Icon';
import NavIcon from './NavIcon';
import InfoTip from './InfoTip';
import RichTextEditor from './RichTextEditor';
import {
  LESSON_CATEGORIES,
  LESSON_CATEGORY_LABELS,
  LESSON_KINDS,
  LESSON_KIND_LABELS,
  hasRecommendation,
  isWorkspaceLesson,
  lessonsForBoard,
  libraryLessons,
  type Lesson,
  type LessonCategory,
  type LessonDraft,
  type LessonKind,
  type LessonLink,
} from '../lessons';
import { EMPTY_RICH_DOC, richHasText, richText, type RichDoc } from '../rich-text';

/**
 * THE LESSONS TAB (board) — 3.19 Learning & Governance spine, L1 (the view).
 *
 * A project's own lessons — what worked, what didn't, a recommendation — so the
 * next project starts from what this one learned. Built to the approved mock
 * (dev-preview/lessons): the board's own lessons newest-first, the workspace-wide
 * ones beneath a "From the workspace" band, each an `lcard` (one `kind` badge +
 * one `category` chip, both the single R3 badge shape), and the R17 add form.
 *
 * The shared add/edit form (`LessonForm`) is exported for the workspace library,
 * which raises lessons with no board.
 */

/** The kind badge's modifier class — the two lesson kinds, two tints (R3). */
const KIND_CLASS: Record<LessonKind, string> = {
  worked: 'worked',
  didnt: 'didnt',
};

function linkIcon(kind: string): IconName {
  switch (kind) {
    case 'signoff':
      return 'signature';
    case 'change-request':
      return 'document';
    case 'budget':
      return 'coin';
    default:
      return 'task';
  }
}

function LessonLinks({ links, extra }: { links?: LessonLink[]; extra?: React.ReactNode }) {
  const revealLink = useApp((s) => s.revealLink);
  const hasLinks = !!links && links.length > 0;
  if (!hasLinks && !extra) return null;
  return (
    <span className="dlinks">
      {hasLinks &&
        links!.map((l) => (
          <button
            type="button"
            key={`${l.kind}:${l.id}`}
            className="lchip"
            onClick={() => revealLink(l)}
            title={`Open ${l.label}`}
          >
            <Icon name={linkIcon(l.kind)} size={13} />
            {l.label}
          </button>
        ))}
      {extra}
    </span>
  );
}

/** One lesson as an `lcard`. `foot` renders the meta row (owner/links, or the
 *  board origin in the library). */
export function LessonCard({
  lesson,
  profiles,
  onEdit,
  onDelete,
  onSeedRisk,
  topExtra,
  linkExtra,
}: {
  lesson: Lesson;
  profiles: ReturnType<typeof useApp.getState>['profiles'];
  onEdit?: () => void;
  onDelete?: () => void;
  /** Seed a risk from this lesson (#490, the learning loop) — opens the register's
   *  editable preview pre-filled from the lesson. Shown only where offered. */
  onSeedRisk?: () => void;
  /** An origin tag shown beside the badges — the library's workspace/board pin. */
  topExtra?: React.ReactNode;
  /** An extra node appended inside the links row — the carry-in panel's "Seed a
   *  risk" future tag. Absent everywhere else, so those cards are unchanged. */
  linkExtra?: React.ReactNode;
}) {
  return (
    <div className="dcard" id={`lesson-${lesson.id}`}>
      <div className="dtop">
        <span className={`kind ${KIND_CLASS[lesson.kind]}`}>
          <span className="dot" />
          {LESSON_KIND_LABELS[lesson.kind]}
        </span>
        <span className="cat">{LESSON_CATEGORY_LABELS[lesson.category]}</span>
        {hasRecommendation(lesson) && (
          <span className="rec-badge">
            <Icon name="sparkle" size={12} /> Recommendation
          </span>
        )}
        {topExtra}
      </div>
      <p className="ltitle">{lesson.title}</p>
      {richHasText(lesson.body) && <p className="lbody">{richText(lesson.body)}</p>}
      {hasRecommendation(lesson) && (
        <p className="lrec">
          <b>Next time:</b> {richText(lesson.recommendation)}
        </p>
      )}
      <div className="dmeta">
        <span className="dwho">
          {lesson.owner ? (
            <>
              Owner <b>{displayName(lesson.owner, profiles)}</b><VendorTag who={lesson.owner} profiles={profiles} compact /> · {asDate(lesson.createdAt)}
            </>
          ) : (
            asDate(lesson.createdAt)
          )}
        </span>
        <LessonLinks links={lesson.links} extra={linkExtra} />
        {(onEdit || onDelete || onSeedRisk) && (
          <span className="dcard-acts">
            {onSeedRisk && (
              <button className="link" onClick={onSeedRisk}>
                Seed a risk
              </button>
            )}
            {onEdit && (
              <button className="link" onClick={onEdit}>
                Edit
              </button>
            )}
            {onDelete && (
              <button className="link" onClick={onDelete}>
                Delete
              </button>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

export default function LessonsView() {
  const lessons = useApp((s) => s.lessons);
  const projectId = useApp((s) => s.projectId);
  const projectName = useApp((s) => s.meta.name);
  const profiles = useApp((s) => s.profiles);
  const role = useApp((s) => s.role);
  const user = useApp((s) => s.user);
  const setView = useApp((s) => s.setView);
  const startRiskSeed = useApp((s) => s.startRiskSeed);
  const deleteLesson = useApp((s) => s.deleteLesson);
  // A writer (rank 3+) may add, edit or remove a lesson ON THIS BOARD — the bar the
  // lessons RLS enforces for a board lesson; a viewer reads them.
  const mayWrite = can(role, 'editIssue');
  // A workspace-wide lesson (shown in the band below) is any member's to add, but
  // only its author or a workspace admin may change or remove it — the same rule
  // the lessons RLS enforces, so no one sees a control that would be refused.
  const me = user?.email?.toLowerCase() ?? '';
  const isAdmin = can(role, 'editSettings');
  const mayManageWorkspace = (l: Lesson) => l.madeBy === me || isAdmin;

  const [form, setForm] = useState<null | { mode: 'add' } | { mode: 'edit'; lesson: Lesson }>(null);

  // A lesson Reveal (e.g. from the after-action report's "captured during this
  // project" list) drills here with the lesson id; scroll to it and flash it, the
  // same reveal the Documents and Plan screens use.
  const revealId = useApp((s) => s.revealId);
  const revealed = useRef<string | null>(null);
  useEffect(() => {
    if (!revealId || revealed.current === revealId || form) return;
    if (!lessons.some((l) => l.id === revealId)) return;
    revealed.current = revealId;
    const el = document.getElementById(`lesson-${revealId}`);
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el?.classList.add('just-revealed');
    const t = window.setTimeout(() => el?.classList.remove('just-revealed'), 1800);
    return () => window.clearTimeout(t);
  }, [revealId, lessons, form]);

  const confirmDelete = async (id: string) => {
    const ok = await confirmDialog({
      title: 'Delete this lesson?',
      body: 'This can’t be undone.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (ok) void deleteLesson(id);
  };

  const own = lessonsForBoard(lessons, projectId);
  const workspace = libraryLessons(lessons).filter(isWorkspaceLesson);

  if (form) {
    return (
      <LessonForm
        key={form.mode === 'add' ? 'add' : form.lesson.id}
        mode={form.mode}
        lesson={form.mode === 'edit' ? form.lesson : undefined}
        contextName={projectName}
        boardId={projectId}
        onClose={() => setForm(null)}
      />
    );
  }

  return (
    <div className="gov">
      <div className="gov-head">
        <div className="gov-head-l">
          <div className="gov-title-row">
            <h1 className="fd-title">Lessons</h1>
            <span className="ihint">
              <InfoTip label="Lessons">
                What this project learned — something that worked or something that didn&rsquo;t,
                with the full story and, if you like, a recommendation for what to do next time,
                so the next project starts from it instead of relearning it.
              </InfoTip>
            </span>
          </div>
          <p className="gov-sub">
            {projectName} · what to carry into the next project · newest first
          </p>
        </div>
        <div className="gov-tools">
          <button className="link" onClick={() => setView('lessonslibrary')}>
            See the workspace library →
          </button>
          {mayWrite && (
            <button className="btn btn-primary" onClick={() => setForm({ mode: 'add' })}>
              <Icon name="plus" size={15} />
              Add a lesson
            </button>
          )}
        </div>
      </div>
      <div className="gov-rule" />

      {own.length === 0 && workspace.length === 0 ? (
        <div className="gov-empty">
          <div className="ge-mark">
            <NavIcon view="lessons" size={24} />
          </div>
          <h2>No lessons captured yet</h2>
          <p>
            A lesson is a generalised, reusable insight — what worked or what didn&rsquo;t, with a
            recommendation if there is one — so the next project starts from what this one learned
            rather than relearning it.
          </p>
          <div className="how">
            <p className="how-h">To add a lesson</p>
            <div className="how-step">
              <Icon name="plus" size={15} />
              <span>
                Click <b>Add a lesson</b>
              </span>
            </div>
            <div className="how-step">
              <NavIcon view="lessons" size={15} />
              <span>Give it a title, say what happened, and add a recommendation if there is one</span>
            </div>
          </div>
          {mayWrite && (
            <div className="btn-row">
              <button className="btn btn-primary" onClick={() => setForm({ mode: 'add' })}>
                <Icon name="plus" size={15} />
                Add a lesson
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          {own.map((l) => (
            <LessonCard
              key={l.id}
              lesson={l}
              profiles={profiles}
              onEdit={mayWrite ? () => setForm({ mode: 'edit', lesson: l }) : undefined}
              onDelete={mayWrite ? () => confirmDelete(l.id) : undefined}
              onSeedRisk={mayWrite ? () => startRiskSeed(l) : undefined}
            />
          ))}

          {workspace.length > 0 && (
            <>
              <div className="band">
                <span className="band-h">From the workspace</span>
                <span className="band-c">{workspace.length}</span>
              </div>
              {workspace.map((l) => (
                <LessonCard
                  key={l.id}
                  lesson={l}
                  profiles={profiles}
                  onEdit={mayManageWorkspace(l) ? () => setForm({ mode: 'edit', lesson: l }) : undefined}
                  onDelete={mayManageWorkspace(l) ? () => confirmDelete(l.id) : undefined}
                  onSeedRisk={mayWrite ? () => startRiskSeed(l) : undefined}
                />
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}

/* ── The add / edit form (R17) — shared with the workspace library ──────────── */

export function LessonForm({
  mode,
  lesson,
  initial,
  contextName,
  boardId,
  onClose,
}: {
  mode: 'add' | 'edit';
  lesson?: Lesson;
  /**
   * PRE-FILL FOR AN ADD — when a lesson is started from somewhere that already
   * knows most of it, e.g. an after-action report's sustain line (a suggested
   * title, the kind, the body or the recommendation, and a link back to the
   * report). The person completes the rest and confirms. Ignored in edit mode,
   * where the lesson itself seeds the fields.
   */
  initial?: {
    title?: string;
    kind?: LessonKind;
    category?: LessonCategory;
    body?: RichDoc;
    recommendation?: RichDoc;
    links?: LessonLink[];
  };
  /** What this form sits in — the project name, or "Workspace" in the library. */
  contextName: string;
  /** Set on a project's own lesson; absent for a workspace-wide one. */
  boardId?: string;
  onClose: () => void;
}) {
  const createLesson = useApp((s) => s.createLesson);
  const editLesson = useApp((s) => s.editLesson);

  const [title, setTitle] = useState(lesson?.title ?? initial?.title ?? '');
  const [kind, setKind] = useState<LessonKind>(lesson?.kind ?? initial?.kind ?? 'worked');
  const [category, setCategory] = useState<LessonCategory>(lesson?.category ?? initial?.category ?? 'scope');
  const [owner, setOwner] = useState(lesson?.owner ?? '');
  const [body, setBody] = useState<RichDoc>(lesson?.body ?? initial?.body ?? EMPTY_RICH_DOC);
  const initialRec = lesson?.recommendation ?? initial?.recommendation;
  // The recommendation is opt-in: the toggle starts on only when one is already there.
  const [hasRec, setHasRec] = useState<boolean>(richHasText(initialRec));
  const [recommendation, setRecommendation] = useState<RichDoc>(initialRec ?? EMPTY_RICH_DOC);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const firstRef = useRef<HTMLInputElement>(null);
  useEffect(() => firstRef.current?.focus(), []);

  const workspaceWide = !boardId;
  const heading = mode === 'edit' ? 'Edit the lesson' : workspaceWide ? 'Add a workspace lesson' : 'Add a lesson';
  const sub = workspaceWide
    ? `${contextName} · an insight that applies to every project · drafted just now`
    : `${contextName} · an insight to carry into the next project · drafted just now`;

  const submit = async () => {
    if (!title.trim()) {
      setProblem('Give the lesson a title.');
      return;
    }
    if (!richHasText(body)) {
      setProblem('Say what happened.');
      return;
    }
    if (hasRec && !richHasText(recommendation)) {
      setProblem('Write the recommendation, or turn it off.');
      return;
    }
    setBusy(true);
    setProblem('');
    // The toggle governs whether a recommendation is saved: off clears it.
    const rec = hasRec ? recommendation : null;
    let err = '';
    if (mode === 'edit' && lesson) {
      err = await editLesson(lesson.id, { title, kind, category, owner, body, recommendation: rec });
    } else {
      const draft: LessonDraft = {
        title,
        kind,
        category,
        body,
        ...(rec ? { recommendation: rec } : {}),
        ...(owner.trim() ? { owner } : {}),
        ...(boardId ? { boardId } : {}),
        // Carry the origin link (e.g. back to the after-action report) so the
        // source can show the line as filed and the register links home.
        ...(initial?.links ? { links: initial.links } : {}),
      };
      err = await createLesson(draft);
    }
    setBusy(false);
    if (err) {
      setProblem(err);
      return;
    }
    onClose();
  };

  return (
    <div className="gov">
      <div className="fd-wrap">
        <div className="fd-head">
          <div className="fd-head-main">
            <h1 className="fd-title">{heading}</h1>
            <p className="fd-sub">{sub}</p>
          </div>
          <div className="fd-head-side">
            <span className={`kind ${KIND_CLASS[kind]}`}>
              <span className="dot" />
              {LESSON_KIND_LABELS[kind]}
            </span>
          </div>
        </div>
        <div className="fd-form">
          <div className="fd-sec">
            <h2 className="fd-sec-h">What kind of lesson</h2>
            <div className="seg seg-wide" style={{ maxWidth: 520 }}>
              {LESSON_KINDS.map((k) => (
                <button
                  key={k}
                  type="button"
                  className={`seg-btn ${kind === k ? 'active' : ''}`}
                  onClick={() => setKind(k)}
                >
                  {LESSON_KIND_LABELS[k]}
                </button>
              ))}
            </div>
            <div className="fd-grid" style={{ marginTop: 16 }}>
              <div className="fd-field">
                <label className="fd-lab">
                  Category<span className="req">*</span>
                </label>
                <select
                  className="input"
                  aria-required
                  value={category}
                  onChange={(e) => setCategory(e.target.value as LessonCategory)}
                >
                  {LESSON_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {LESSON_CATEGORY_LABELS[c]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="fd-field">
                <label className="fd-lab">Owner</label>
                <input
                  className="input"
                  value={owner}
                  onChange={(e) => setOwner(e.target.value)}
                  placeholder="Who carries the follow-up"
                />
              </div>
            </div>
          </div>

          <div className="fd-sec">
            <h2 className="fd-sec-h">The lesson</h2>
            <div className="fd-grid">
              <div className="fd-field fd-wide">
                <label className="fd-lab">
                  Title<span className="req">*</span>
                </label>
                <input
                  ref={firstRef}
                  className="input"
                  aria-required
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="The lesson in a few words."
                />
                <p className="hintline">A short headline — what the lesson is, so it reads at a glance.</p>
              </div>
              <div className="fd-field fd-wide">
                <label className="fd-lab">
                  What happened<span className="req">*</span>
                </label>
                <RichTextEditor
                  value={body}
                  onChange={setBody}
                  ariaLabel="What happened"
                  ariaRequired
                  placeholder="What happened, and why it mattered — as much as it takes."
                />
                <p className="hintline">The full story — format it, add the detail a reader needs.</p>
              </div>
            </div>
          </div>

          <div className="fd-sec">
            <div className="lesson-rec-row">
              <div className="lesson-rec-lab">
                <h2 className="fd-sec-h" style={{ margin: 0 }}>Recommendation</h2>
                <p className="lesson-rec-why">Tie a recommendation to this lesson — what to do next time.</p>
              </div>
              <button
                type="button"
                className={`switch ${hasRec ? 'on' : ''}`}
                role="switch"
                aria-checked={hasRec}
                aria-label="Add a recommendation"
                onClick={() => setHasRec((v) => !v)}
              >
                <span className="switch-knob" />
              </button>
            </div>
            {hasRec && (
              <div className="fd-field fd-wide" style={{ marginTop: 14 }}>
                <label className="fd-lab">
                  What to do next time<span className="req">*</span>
                </label>
                <RichTextEditor
                  value={recommendation}
                  onChange={setRecommendation}
                  ariaLabel="What to do next time"
                  ariaRequired
                  placeholder="The recommendation the next project acts on."
                />
                <p className="hintline">The forward action — what the next project should do about it.</p>
              </div>
            )}
          </div>

          {problem && <p className="hintline" role="alert">{problem}</p>}

          <div className="fd-actions">
            <button className="btn btn-primary" onClick={submit} disabled={busy}>
              {mode === 'edit' ? 'Save changes' : workspaceWide ? 'Add workspace lesson' : 'Add lesson'}
            </button>
            <button className="btn" onClick={onClose} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
